import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import CloseIcon from '@mui/icons-material/Close';

interface InsertImageButtonProps {
  onAttachmentChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDeleteAttachment: () => void;
  imgFile: File | null;
}

const InsertImageButton = ({
  onDeleteAttachment,
  onAttachmentChange,
  imgFile,
}: InsertImageButtonProps) => {
  const [insertImgBtnAnchorEl, setInsertImgBtnAnchorEl] =
    useState<HTMLDivElement | null>(null);

  const imgInput = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Boolean(imgFile)) {
      setInsertImgBtnAnchorEl(buttonRef.current);
    }
  }, [imgFile]);

  return (
    <>
      <motion.div
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
        onClick={(e) => setInsertImgBtnAnchorEl(e.currentTarget)}
        ref={buttonRef}
      >
        <Box id="insert-image" sx={{ marginRight: '10px' }}>
          <Tooltip title="Insert image" placement="top" enterDelay={1000}>
            <span>
              <IconButton
                disableRipple
                sx={{
                  p: 0,
                  color: Boolean(insertImgBtnAnchorEl)
                    ? 'primary.main'
                    : '#5a5a5a',
                  '&:hover': { color: 'primary.main' },
                }}
                size="small"
              >
                <AddPhotoAlternateIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </motion.div>

      <Popover
        open={Boolean(insertImgBtnAnchorEl)}
        anchorEl={insertImgBtnAnchorEl}
        onClose={() => setInsertImgBtnAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          {Boolean(imgFile) ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'warning.main',
                  borderRadius: '8px',
                  mr: 1,
                }}
              >
                <CropOriginalIcon />
              </Box>
              <Typography variant="subtitle1" sx={{ wordWrap: 'no-wrap' }}>
                {imgFile?.name}
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'error.main',
                  borderRadius: '100px',
                  width: 'fit-content',
                  height: 'fit-content',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ml: 3,
                  cursor: 'pointer',
                }}
                onClick={onDeleteAttachment}
              >
                <CloseIcon fontSize="inherit" sx={{ m: 0.25 }} />
              </Box>
            </Box>
          ) : (
            <motion.div whileTap={{ scale: 0.9 }}>
              <input
                type="file"
                accept="image/*"
                ref={imgInput}
                style={{ display: 'none' }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onAttachmentChange(e);
                }}
              />
              <Box
                sx={{ border: '1px dashed #5a5a5a', borderRadius: '12px' }}
                onClick={() => imgInput.current?.click()}
              >
                <Box
                  sx={{ py: 2, px: 4, display: 'flex', alignItems: 'center' }}
                >
                  <CropOriginalIcon sx={{ mr: 0.5 }} />
                  <Typography variant="subtitle1">Insert Your Image</Typography>
                </Box>
              </Box>
            </motion.div>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default InsertImageButton;
